// Debounced alert: batches unique messages for a short window, then cools down.
// - Collect phase: gathers messages for `collectWindowMs` (default 200ms).
// - Show: displays a single alert with a bullet list of unique messages.
// - Cooldown: ignores new messages for `cooldownMs` (default 200ms) after user confirms.

export interface DebouncedAlertOptions {
  collectWindowMs?: number; // time to batch incoming messages
  cooldownMs?: number; // time to ignore new messages after alert closes
  title?: string; // optional title line shown above messages
}

export function createDebouncedAlert(opts?: DebouncedAlertOptions) {
  const collectWindowMs = opts?.collectWindowMs ?? 200;
  const cooldownMs = opts?.cooldownMs ?? 200;
  const title = opts?.title;

  const queue = new Set<string>();
  let collectTimer: number | null = null;
  let inCooldown = false;

  const startCollectTimer = () => {
    if (collectTimer !== null) return;
    collectTimer = window.setTimeout(() => {
      collectTimer = null;

      const items = Array.from(queue);
      queue.clear();

      if (items.length === 0) return;

      const body
        = items.length === 1
          ? items[0]
          : items.map(m => `• ${m}`).join('\n');

      const text = title ? `${title}\n\n${body}` : body;

      // Blocking alert; execution resumes after user clicks OK.
      window.alert(text);

      // Begin cooldown: ignore messages for the specified duration.
      inCooldown = true;
      window.setTimeout(() => {
        inCooldown = false;
      }, cooldownMs);
    }, collectWindowMs);
  };

  return (message: string) => {
    // Silently drop messages during cooldown.
    if (inCooldown) return;

    // Add unique message to the queue.
    queue.add(message);

    // Ensure a timer is running to flush after the collect window.
    startCollectTimer();
  };
}

// Singleton with defaults (200ms collect, 200ms cooldown).
export const debouncedAlert = createDebouncedAlert();
